import { useEffect, useRef, useState, useMemo } from "react";
import { Search, MapPin, X, Loader2, User2 } from "lucide-react";
import { searchPlaces } from "@/lib/api";

export default function SearchBar({ onSelectPlace, contacts = [], onSelectContact }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  // Local contact matches (synchronous, fuzzy on name/town/postcode)
  const contactMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 1) return [];
    return contacts
      .filter((c) => {
        const hay = [c.name, c.town, c.region, c.postcode, c.email, c.phone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 5);
  }, [query, contacts]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    // Contact matches show up instantly
    if (contactMatches.length > 0) setOpen(true);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const data = await searchPlaces(query);
        setResults(data);
        setOpen(true);
        setActive(0);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, contactMatches.length]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("touchstart", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("touchstart", onClickOutside);
    };
  }, []);

  const pick = (place) => {
    onSelectPlace?.(place);
    setQuery(place.short);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="pointer-events-auto w-full max-w-xl"
      data-testid="search-bar-wrapper"
    >
      <div
        className={`relative flex items-center gap-3 rounded-full border bg-[#0a0a0a]/80 backdrop-blur-xl px-5 h-14 transition-all duration-300 ${
          open || query ? "border-[#00e5ff] shadow-[0_0_18px_rgba(0,229,255,0.25)]" : "border-white/10"
        }`}
      >
        <Search className="h-5 w-5 text-[#00e5ff] shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={handleKey}
          placeholder="SEARCH TOWNS, CITIES, POSTCODES…"
          className="flex-1 bg-transparent text-white placeholder:text-white/30 font-mono text-sm tracking-wider outline-none uppercase"
          data-testid="search-input"
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <Loader2 className="h-4 w-4 text-[#00e5ff] animate-spin" />}
        {query && !loading && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              setOpen(false);
            }}
            className="text-white/40 hover:text-white"
            data-testid="search-clear-button"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (contactMatches.length > 0 || results.length > 0) && (
        <div
          className="mt-2 rounded-xl border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl shadow-2xl overflow-hidden cyber-corners"
          data-testid="search-results"
        >
          <ul className="max-h-96 overflow-auto thin-scroll">
            {contactMatches.length > 0 && (
              <li className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-widest font-mono text-white/40">
                Contacts
              </li>
            )}
            {contactMatches.map((c, idx) => (
              <li key={`c-${c.id}`}>
                <button
                  onClick={() => {
                    onSelectContact?.(c);
                    setQuery("");
                    setResults([]);
                    setOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 flex items-center gap-3 transition-colors text-white/85 hover:bg-[#00e5ff]/10 hover:text-[#00e5ff]"
                  data-testid={`search-contact-${c.id}`}
                >
                  {c.avatar ? (
                    <img
                      src={c.avatar}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover border border-white/15 shrink-0"
                      draggable={false}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-white/5 border border-white/15 flex items-center justify-center shrink-0">
                      <User2 className="h-4 w-4 text-[#00e5ff]" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-display uppercase tracking-wider truncate">
                      {c.name}
                    </div>
                    <div className="text-[11px] text-white/50 truncate font-mono">
                      {[c.town, c.region, c.postcode].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                </button>
              </li>
            ))}
            {results.length > 0 && (
              <li className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-widest font-mono text-white/40 border-t border-white/10">
                Places
              </li>
            )}
            {results.map((r, idx) => (
              <li key={`p-${r.id}`}>
                <button
                  onClick={() => pick(r)}
                  onMouseEnter={() => setActive(idx)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                    idx === active ? "bg-[#00e5ff]/10 text-[#00e5ff]" : "text-white/80 hover:bg-white/5"
                  }`}
                  data-testid={`search-result-${idx}`}
                >
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#f5d300]" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold uppercase tracking-wide truncate font-display">
                      {r.short}
                    </div>
                    <div className="text-xs text-white/50 truncate font-mono">{r.name}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && !loading && query && results.length === 0 && contactMatches.length === 0 && (
        <div className="mt-2 rounded-xl border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl p-4 text-sm text-white/50 font-mono">
          No matches in England for &quot;{query}&quot;.
        </div>
      )}
    </div>
  );
}
